import * as R from 'ramda';
import { Observable, OperatorFunction } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';

// Matches whole number 0 1 2 33 47, etc,
export const wholeNumber = /^\d+$/;

export const toInt = (x: string) => parseInt(x, 10);

/**
 * Returns a partial copy of an object containing only the keys that satisfy the supplied predicates.
 * If key is not in predicate, it is ignored. It is like R.pick() + R.where()
 */
export const pickWhere = R.curry((predicates: any, obj) =>
    R.pickBy((val, key) => R.has(key, predicates) && predicates[key](val), obj)
);

export function select<T, S>(callback: (value: T, index: number) => S): OperatorFunction<T, S> {
    // https://stackoverflow.com/questions/47198706/rxjs-pipe-and-lettable-operator-map-this-context-of-type-void-is-not-assi/47198895
    return function _select(source: Observable<T>) {
        return source.pipe(
            map(callback),
            distinctUntilChanged()
        );
    };
}

export function isNumeric(value: any): boolean {
    return !isNaN(value);
}

export function hasUppercase(str: string): boolean {
    return str.toLowerCase() !== str;
}

export function hasNumber(str: string): boolean {
    return /\d/.test(str);
}

export function hasSpecialChars(str: string): boolean {
    const format = /[!@#$%^&*(),.?":{}|<>]+/;
    return format.test(str);
}

/**
 *
 * @param str String in which you want to check whether given string present
 * @param subStr Substring that you want to find
 */
function _hasString(str: string, subStr: string): boolean {
    return R.toLower(str).includes(R.toLower(subStr));
}

export const hasString = R.curry(_hasString);

/**
 * Checks if given `email` is public. Can generate false **negative**.
 * It could return `false` for valid public email address.
 * @export
 * @param {string} email
 * @returns `true` if given email is public.
 */
export function isPublicEmail(email: string) {
    const domain = email.substring(email.lastIndexOf('@') + 1);

    return publicDomains.some((x) => x.test(domain));
}

export function isPrivateEmail(email: string) {
    return !isPublicEmail(email);
}

const publicDomains = [
    /gmail/,
    /yahoo/,
    /rediff/,
    /rediffmail/,
    /indiatimes/,
    /outlook/,
    /hotmail/,
    /aol/,
    /zoho/,
    /^(mail\.)/,
    /gmx/,
    /protonmail/,
    /yandex/,
    /icloud/,
    /tutanota/
];
